import { createReducer, on } from '@ngrx/store';
import { SchedulerCrewDto } from '../../api/scheduler/models/scheduler-crew-dto';
import { PageMeta } from '../../api/scheduler/models/page-meta';
import { schedulerCrewEditableFieldsEqual } from '../../features/edit-crew/utils/scheduler-crew.mapper';
import {
  DEFAULT_SCHEDULER_CREW_QUERY,
  SchedulerCrewActions,
  SchedulerCrewQuery,
} from './scheduler-crew.actions';

export interface SchedulerCrewState {
  items: SchedulerCrewDto[];
  page: PageMeta | null;
  query: SchedulerCrewQuery;
  loading: boolean;
  /** True while a "load more" page is in flight; its rows are appended to `items`. */
  appending: boolean;
  loadMoreError: boolean;
  listRequestId: number;
  error: string | null;
  updatingIds: number[];
  cardErrors: Record<number, string>;
  updateRequests: Record<number, number>;
}

export const initialSchedulerCrewState: SchedulerCrewState = {
  items: [],
  page: null,
  query: DEFAULT_SCHEDULER_CREW_QUERY,
  loading: false,
  appending: false,
  loadMoreError: false,
  listRequestId: 0,
  error: null,
  updatingIds: [],
  cardErrors: {},
  updateRequests: {},
};

export const schedulerCrewReducer = createReducer(
  initialSchedulerCrewState,
  on(SchedulerCrewActions.load, (state, { query, append }) => ({
    ...state,
    loading: true,
    appending: !!append,
    loadMoreError: false,
    error: null,
    listRequestId: state.listRequestId + 1,
    query: { ...state.query, ...query },
  })),
  on(SchedulerCrewActions.setSearch, (state, { search }) => ({
    ...state,
    query: { ...state.query, search, page: 0 },
  })),
  on(SchedulerCrewActions.reset, (state) => ({
    ...state,
    loading: true,
    appending: false,
    loadMoreError: false,
    error: null,
    listRequestId: state.listRequestId + 1,
    query: DEFAULT_SCHEDULER_CREW_QUERY,
  })),
  on(SchedulerCrewActions.loadSuccess, (state, { items, page, requestId }) => {
    if (requestId !== state.listRequestId) {
      return state;
    }
    return {
      ...state,
      loading: false,
      appending: false,
      items: state.appending
        ? [...state.items, ...items.filter((n) => !state.items.some((o) => o.id === n.id))]
        : items,
      page,
      error: null,
    };
  }),
  on(SchedulerCrewActions.loadFailure, (state, { error, requestId }) => {
    if (requestId !== undefined && requestId !== state.listRequestId) {
      return state;
    }
    if (state.appending) {
      // Keep the rows already loaded; surface a retry instead of replacing the list with an error.
      return { ...state, loading: false, appending: false, loadMoreError: true };
    }
    return {
      ...state,
      loading: false,
      error,
    };
  }),
  on(SchedulerCrewActions.update, (state, { id, rank, wingType, rotation }) => {
    const current = state.items.find((item) => item.id === id);
    if (
      current &&
      schedulerCrewEditableFieldsEqual(current, { ...current, rank, wingType, rotation })
    ) {
      return state;
    }
    const requestId = (state.updateRequests[id] ?? 0) + 1;
    return {
      ...state,
      updatingIds: state.updatingIds.includes(id) ? state.updatingIds : [...state.updatingIds, id],
      updateRequests: { ...state.updateRequests, [id]: requestId },
      cardErrors: omitCardError(state.cardErrors, id),
      items: state.items.map((item) =>
        item.id === id ? { ...item, rank, wingType, rotation } : item
      ),
    };
  }),
  on(SchedulerCrewActions.updateSuccess, (state, { id, crew, requestId }) => {
    if (state.updateRequests[id] !== requestId) {
      return state;
    }
    return {
      ...state,
      updatingIds: state.updatingIds.filter((itemId) => itemId !== id),
      items: state.items.map((item) => {
        if (item.id !== id) {
          return item;
        }
        const next = { ...item, ...crew, id };
        return schedulerCrewEditableFieldsEqual(item, next) ? item : next;
      }),
      cardErrors: omitCardError(state.cardErrors, id),
    };
  }),
  on(SchedulerCrewActions.updateFailure, (state, { id, error, requestId, previous }) => {
    if (state.updateRequests[id] !== requestId) {
      return {
        ...state,
        cardErrors: { ...state.cardErrors, [id]: error },
      };
    }
    return {
      ...state,
      updatingIds: state.updatingIds.filter((itemId) => itemId !== id),
      items: previous ? state.items.map((item) => (item.id === id ? previous : item)) : state.items,
      cardErrors: { ...state.cardErrors, [id]: error },
    };
  }),
  on(SchedulerCrewActions.updateStatus, (state, { id, status }) => {
    const current = state.items.find((item) => item.id === id);
    if (current && current.status === status) {
      return state;
    }
    const requestId = (state.updateRequests[id] ?? 0) + 1;
    return {
      ...state,
      updatingIds: state.updatingIds.includes(id) ? state.updatingIds : [...state.updatingIds, id],
      updateRequests: { ...state.updateRequests, [id]: requestId },
      cardErrors: omitCardError(state.cardErrors, id),
      items: state.items.map((item) => (item.id === id ? { ...item, status } : item)),
    };
  }),
  on(SchedulerCrewActions.updateStatusSuccess, (state, { id, crew, requestId }) => {
    if (state.updateRequests[id] !== requestId) {
      return state;
    }
    return {
      ...state,
      updatingIds: state.updatingIds.filter((itemId) => itemId !== id),
      items: state.items.map((item) => (item.id === id ? { ...item, ...crew, id } : item)),
      cardErrors: omitCardError(state.cardErrors, id),
    };
  }),
  on(SchedulerCrewActions.updateStatusFailure, (state, { id, error, requestId, previous }) => {
    if (state.updateRequests[id] !== requestId) {
      return {
        ...state,
        cardErrors: { ...state.cardErrors, [id]: error },
      };
    }
    return {
      ...state,
      updatingIds: state.updatingIds.filter((itemId) => itemId !== id),
      items: previous ? state.items.map((item) => (item.id === id ? previous : item)) : state.items,
      cardErrors: { ...state.cardErrors, [id]: error },
    };
  }),
  on(SchedulerCrewActions.setCardError, (state, { id, error }) => ({
    ...state,
    cardErrors: { ...state.cardErrors, [id]: error },
  })),
  on(SchedulerCrewActions.clearCardError, (state, { id }) => ({
    ...state,
    cardErrors: omitCardError(state.cardErrors, id),
  }))
);

function omitCardError(errors: Record<number, string>, id: number): Record<number, string> {
  const next = { ...errors };
  delete next[id];
  return next;
}
